const normalizeTag = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
const generalTags = new Set(['accesorios', 'disfraces', 'disfraz', 'mascaras', 'mascara', 'halloween', 'adulto', 'adultos', 'nino', 'ninos', 'nina', 'ninas', 'hombre', 'mujer', 'unisex', 'variantes de color', 'maquillaje', 'pelucas', 'decoracion', 'rojo', 'negro', 'blanco', 'azul', 'verde', 'rosa', 'amarillo', 'morado', 'naranja', 'dorado', 'plateado', 'goma', 'plastico']);
const themeTags = source => (source.tags || []).map(normalizeTag).filter(tag => tag.length > 2 && !generalTags.has(tag));

class ProductComplements extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    if (this.querySelector('[data-complement-product]') || !this.dataset.url) return;
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 160px 0px' });
      this.observer.observe(this);
    } else this.load();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.request?.abort();
    clearTimeout(this.timeout);
    this.revision = (this.revision || 0) + 1;
  }

  async load() {
    this.request?.abort();
    clearTimeout(this.timeout);
    const revision = this.revision = (this.revision || 0) + 1;
    const request = this.request = new AbortController();
    this.timeout = setTimeout(() => request.abort(), 12000);
    try {
      const url = new URL(this.dataset.url, location.origin);
      if (url.origin !== location.origin) return;
      this.sources = JSON.parse(this.dataset.sources || '[]');
      if (!this.sources.length || this.sources[0].family === 'other') { this.hidden = true; return; }
      const excluded = JSON.parse(this.dataset.exclude || '[]');
      this.excluded = new Set([...excluded, ...this.sources.map(source => source.id)].map(String));
      const keywords = [...new Set(this.sources.flatMap(source => {
        const tags = themeTags(source);
        const specific = tags.filter(tag => tag.includes(' '));
        return specific.length ? specific : tags;
      }))].slice(0, 6);
      if (keywords.length && this.dataset.searchUrl) {
        const search = new URL(this.dataset.searchUrl, location.origin);
        if (search.origin === location.origin) {
          const familyQuery = this.sources[0].family === 'accessory'
            ? '(tag:"Disfraces" OR tag:"Mascaras" OR tag:"Máscaras")'
            : 'tag:"Accesorios" -tag:"Disfraces" -tag:"Mascaras" -tag:"Máscaras"';
          const tagsQuery = keywords.map(tag => `tag:"${tag.replace(/["\\]/g, '')}"`).join(' OR ');
          search.searchParams.set('q', `${familyQuery} (${tagsQuery})`);
          search.searchParams.set('options[unavailable_products]', 'hide');
          const content = await this.fetchContent(search, request.signal);
          if (request.signal.aborted || revision !== this.revision || !this.isConnected) return;
          if (content && this.present(content, revision, true)) return;
        }
      }
      for (const intent of ['complementary', 'related']) {
        url.searchParams.set('intent', intent);
        const content = await this.fetchContent(url, request.signal);
        if (request.signal.aborted || revision !== this.revision || !this.isConnected) return;
        if (content?.dataset.productId !== this.dataset.productId) return;
        if (this.present(content, revision)) return;
      }
      this.hidden = true;
    } catch {
      if (revision === this.revision && this.isConnected) this.hidden = true;
    } finally {
      if (revision === this.revision) clearTimeout(this.timeout);
    }
  }

  async fetchContent(url, signal) {
    const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
    if (!response.ok || new URL(response.url).origin !== location.origin) return null;
    // Only this theme's server-rendered section fragments enter the commerce UI.
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    return parsed.querySelector('[data-complement-search], [data-complements-response]');
  }

  present(content, revision, requireTheme = false) {
    if (revision !== this.revision || !this.isConnected || this.request.signal.aborted) return false;
    const accessorySource = this.sources[0].family === 'accessory';
    const seen = new Set(this.excluded);
    const ranked = [...content.querySelectorAll('[data-complement-product]')].map((card, index) => {
      const family = card.dataset.complementFamily;
      const tags = JSON.parse(card.dataset.complementTags || '[]').map(normalizeTag);
      const score = this.sources.reduce((total, source) => total + themeTags(source).filter(tag => tags.includes(tag)).length, 0);
      const opposite = accessorySource ? family === 'costume' || family === 'mask' : family === 'accessory';
      return { card, score, index, eligible: opposite && (!requireTheme || score > 0) };
    }).filter(row => row.eligible).sort((a, b) => b.score - a.score || a.index - b.index);
    const selected = [];
    for (const row of ranked) {
      const id = row.card.dataset.complementProduct;
      if (seen.has(id)) continue;
      seen.add(id);
      selected.push(row.card);
      if (selected.length >= Math.min(4, Number(this.dataset.limit) || 4)) break;
    }
    if (!selected.length) return false;
    const list = this.querySelector('[data-complement-list]');
    if (!list) return false;
    selected.forEach(card => card.querySelectorAll('[data-entrance-card]').forEach(element => element.setAttribute('data-entrance-ready', '')));
    list.replaceChildren(...selected);
    list.closest('section').hidden = false;
    this.hidden = false;
    return true;
  }
}

if (!customElements.get('product-complements')) customElements.define('product-complements', ProductComplements);
